import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDeviceDesktop,
  IconEdit,
  IconPlus,
  IconPower,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeleteDevice, useDevices, useUpdateDevice } from './devicesApi';
import { DeviceFormDrawer } from './DeviceFormDrawer';
import type { DeviceDto } from './types';

export function DevicesPage() {
  const { t, i18n } = useTranslation();
  const { data: devices, isLoading } = useDevices();
  const deleteMutation = useDeleteDevice();
  const updateMutation = useUpdateDevice();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<DeviceDto | null>(null);
  const [deleting, setDeleting] = useState<DeviceDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (device: DeviceDto) => {
    setEditing(device);
    setDrawerOpen(true);
  };

  const toggleActive = async (device: DeviceDto) => {
    try {
      await updateMutation.mutateAsync({
        id: device.deviceId,
        request: { name: device.name, serial: device.serial, isActive: !device.isActive },
      });
      notifications.show({
        color: 'green',
        title: t('common.toasts.updated'),
        message: device.name,
      });
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.deviceId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.name,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasDevices = (devices?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.organization')}
        title={t('devices.page.title')}
        subtitle={t('devices.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('devices.page.create')}
          </Button>
        }
      />

      <Paper p={hasDevices ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasDevices ? (
          <EmptyState
            icon={<IconDeviceDesktop size={22} stroke={1.6} />}
            title={t('devices.empty.title')}
            description={t('devices.empty.body')}
            actionLabel={t('devices.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('devices.columns.name')}</Table.Th>
                <Table.Th>{t('devices.columns.branch')}</Table.Th>
                <Table.Th>{t('devices.columns.serial')}</Table.Th>
                <Table.Th>{t('devices.columns.status')}</Table.Th>
                <Table.Th>{t('devices.columns.lastSeen')}</Table.Th>
                <Table.Th style={{ width: 140, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {devices!.map((d) => (
                <Table.Tr key={d.deviceId}>
                  <Table.Td fw={500}>{d.name}</Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{d.branchNameAr}</Table.Td>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {d.serial ?? '—'}
                    </span>
                  </Table.Td>
                  <Table.Td>
                    {d.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)" className="font-numeric">
                    {formatLastSeen(d.lastSeenAt, i18n.language)}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip
                        label={d.isActive ? t('devices.actions.deactivate') : t('devices.actions.activate')}
                        withinPortal
                      >
                        <ActionIcon
                          variant="subtle"
                          color={d.isActive ? 'orange' : 'green'}
                          onClick={() => toggleActive(d)}
                          aria-label={d.isActive ? t('devices.actions.deactivate') : t('devices.actions.activate')}
                        >
                          <IconPower size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(d)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(d)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <DeviceFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        device={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('devices.delete.title')}
        message={t('devices.delete.body', { name: deleting?.name ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function formatLastSeen(iso: string | null, language: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-PS' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    numberingSystem: 'latn',
  }).format(new Date(iso));
}
